export default function AlternativesSection() {
  const alternatives = [
    {
      title: 'Loan Modification',
      description: 'Your lender agrees to change your loan terms to make payments more affordable.',
      pros: ['Keep your home', 'Lower monthly payments', 'Avoid foreclosure'],
      cons: ['Difficult to qualify', 'Takes 3-6 months', 'Not guaranteed', 'Extends loan term'],
      icon: 'ri-file-edit-line',
      success: 'Low'
    },
    {
      title: 'Forbearance Agreement',
      description: 'Your lender temporarily reduces or suspends payments while you recover financially.',
      pros: ['Temporary relief', 'Avoid immediate foreclosure', 'Time to recover'],
      cons: ['Payments still owed later', 'Temporary solution only', 'Balloon payment due', 'Doesn\'t solve underlying problem'],
      icon: 'ri-pause-circle-line',
      success: 'Medium'
    },
    {
      title: 'Short Sale',
      description: 'Sell your home for less than you owe with lender approval.',
      pros: ['Avoid foreclosure', 'Less credit damage', 'Lender forgives difference'],
      cons: ['Takes 4-6 months', 'Lender must approve', 'Still damages credit', 'Complex process'],
      icon: 'ri-home-4-line',
      success: 'Medium'
    },
    {
      title: 'Deed in Lieu',
      description: 'Voluntarily transfer your property deed to the lender to avoid foreclosure.',
      pros: ['Avoid foreclosure auction', 'Faster than foreclosure', 'May negotiate moving costs'],
      cons: ['Still lose your home', 'Credit damage', 'Possible deficiency', 'Lender must agree'],
      icon: 'ri-file-transfer-line',
      success: 'Low'
    },
    {
      title: 'Bankruptcy',
      description: 'File Chapter 7 or 13 bankruptcy to temporarily stop foreclosure.',
      pros: ['Stops foreclosure temporarily', 'Time to reorganize', 'Discharge other debts'],
      cons: ['Severe credit damage', 'Expensive legal fees', 'Public record', 'Only delays foreclosure'],
      icon: 'ri-scales-3-line',
      success: 'Low'
    },
    {
      title: 'Sell to Cash Buyer',
      description: 'Sell your home quickly to a cash buyer like TRex RE LLC - The Texas Property buyers before foreclosure.',
      pros: ['Close in 7 days', 'Get cash at closing', 'Minimal credit damage', 'No repairs needed', 'No fees or commissions', 'Guaranteed sale'],
      cons: ['Must sell your home'],
      icon: 'ri-money-dollar-circle-line',
      success: 'High'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Alternatives to Foreclosure: What Are Your Options?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            You have several options to avoid foreclosure in El Paso. Here's an honest comparison of each alternative, including the pros, cons, and likelihood of success.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {alternatives.map((alt, index) => (
            <div key={index} className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all ${
              alt.success === 'High' ? 'border-4 border-teal-600 relative' : 'border-2 border-gray-200'
            }`}>
              {alt.success === 'High' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                  <i className="ri-star-fill mr-1"></i>
                  Best Option
                </div>
              )}
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-6">
                <i className={`${alt.icon} text-3xl text-gray-900`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{alt.title}</h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">{alt.description}</p>
              
              <div className="mb-6">
                <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Pros
                </h4>
                <ul className="space-y-2">
                  {alt.pros.map((pro, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center">
                  <i className="ri-close-line mr-2"></i>
                  Cons
                </h4>
                <ul className="space-y-2">
                  {alt.cons.map((con, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <i className="ri-close-circle-fill text-red-600 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                alt.success === 'High' ? 'bg-green-100 text-green-800' :
                alt.success === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                Success Rate: {alt.success}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-black text-gray-900 mb-8 text-center">
            Why Selling to a Cash Buyer Is Often the Best Choice
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Speed and Certainty</h4>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                Most alternatives to avoid foreclosure take months and have uncertain outcomes. Loan modifications can take 3-6 months and are often denied. Short sales require lender approval and buyer financing. Bankruptcy only delays foreclosure temporarily.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                When you sell to TRex RE LLC - The Texas Property buyers, we close in 7 days with guaranteed cash. There's no waiting, no uncertainty, and no risk of the deal falling through. You avoid foreclosure with certainty.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Financial Benefits</h4>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                Traditional alternatives often leave you with nothing or even owing money. Foreclosure gives you $0. Deed in lieu gives you $0. Bankruptcy costs thousands in legal fees. Short sales take months and may still result in deficiency judgments.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                We pay cash at closing, cover all costs, and you walk away with money in your pocket. You can use this cash to relocate, pay off debts, or start fresh. It's the only option where you actually receive money to help you avoid foreclosure and move forward.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}